import { MAX_CHARACTERS_PASSWORD, MAX_CHARACTERS_SM, MIN_CHARACTERS_PASSWORD } from "../../constants.ts";
import moreappIconBrand from "../../assets/moreapp-icon-brand.svg";
import { useTranslation } from "../../state/i18n.tsx";
import { createForm } from "../../components/form/createForm.ts";
import type { InferOutput } from "valibot";
import { boolean, object } from "valibot";
import { email, textValue } from "../../utils/validation/base.ts";
import { Form } from "../../components/form/Form.tsx";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import { createSignal, Show } from "solid-js";
import { Checkbox } from "../../components/checkbox/Checkbox.tsx";
import { Translation } from "../../utils/Translation.tsx";
import { keyFromSelector } from "i18next";
import TextLink from "../../components/link/TextLink.tsx";
import { TextButton } from "../../components/button/Button.tsx";
import InlineMessage from "../../components/inlineMessage/InlineMessage.tsx";
import { directUserToSupport } from "../../utils/supportUtil.ts";
import { get, isEmpty } from "lodash-es";
import type { MoreAppError } from "../../MoreAppError.ts";
import type { SignupRequest } from "../../services/SignupService.ts";
import SignupService from "../../services/SignupService.ts";
import { useSearchParams } from "@solidjs/router";
import Text from "../../components/text/Text.tsx";
import { PasswordInput } from "../../components/inputs/PasswordInput.tsx";
import CountriesService from "../../services/CountriesService.ts";

type SignupSearchParams = {
  plan: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
};

type SignupFormProps = {
  onSignup: (data: SignupRequest) => void;
};

const SignupForm = (props: SignupFormProps) => {
  const { t, currentLanguage } = useTranslation();
  const signupMutation = SignupService.signup();
  const countries = CountriesService.getCountries();
  const country = countries.data?.current?.alpha2Code ?? "NL";
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // In the signup the segment defaults to OTHER. Users can amend this in the onboarding
  const segment = "OTHER";
  const [{ plan, utm_source, utm_medium, utm_campaign, utm_content, utm_term }] = useSearchParams<SignupSearchParams>();

  const signupSchema = object({
    emailAddress: email(MAX_CHARACTERS_SM),
    password: textValue(MIN_CHARACTERS_PASSWORD, MAX_CHARACTERS_PASSWORD),
    acceptTerms: boolean(),
  });
  type Signup = InferOutput<typeof signupSchema>;

  const form = createForm<Signup>(
    signupSchema,
    async ({ emailAddress, password }) => {
      setSubmitError(null);
      const data: SignupRequest = {
        customerName: emailAddress,
        emailAddress,
        password,
        country,
        language: currentLanguage(),
        timeZone,
        segment,
        plan,
        utmParams: {
          source: utm_source,
          medium: utm_medium,
          campaign: utm_campaign,
          content: utm_content,
          term: utm_term,
        },
      };
      await signupMutation.mutateAsync(data, {
        onSuccess: () => props.onSignup(data),
        onError: (err) => {
          // The backend has no dedicated error code for this case (scope is the generic
          // "INVALID_REQUEST_DATA"), so matching on the message text is the only option.
          const isPasswordBlacklistedError = get(err.rawData, "details.fieldErrors.password", [""]).includes(
            "Password is too easy",
          );
          const isEmailAddressNotUniqueError = get(err.rawData, "details.fieldErrors.emailAddress", [""]).includes(
            "The email address is not unique",
          );
          if (isPasswordBlacklistedError) {
            setPasswordBlacklisted(true);
          }
          if (isEmailAddressNotUniqueError) {
            setEmailAdressNotUnique(true);
          }
          if (isPasswordBlacklistedError || isEmailAddressNotUniqueError) {
            return;
          }
          setSubmitError(err);
        },
      });
    },
    { emailAddress: undefined, password: undefined, acceptTerms: false },
  );
  const [submitError, setSubmitError] = createSignal<MoreAppError | null>(null);
  const [passwordBlacklisted, setPasswordBlacklisted] = createSignal(false);
  const [emailAdressNotUnique, setEmailAdressNotUnique] = createSignal(false);

  return (
    <div class="flex flex-col gap-20">
      <div class="flex flex-col items-center gap-10 desktop:items-start">
        <img src={moreappIconBrand} class="size-12" alt={t(($) => $.MOREAPP_LOGO_IMG_ALT)} />
        <div class="flex flex-col gap-1 text-center desktop:text-left">
          <Text as="h1" class="text-header-3xl text-primary" text={t(($) => $.SIGNUP_PAGE_FORM_TITLE)} />
          <Text class="text-medium-regular text-secondary" text={t(($) => $.SIGNUP_PAGE_FORM_SUBTITLE)} />
        </div>
        <Form class="flex w-full flex-col gap-y-6" form={form}>
          {({ value, updateValue, error }) => (
            <>
              <TextInput
                label={t(($) => $.SIGNUP_PAGE_FORM_WORK_EMAIL_LABEL)}
                value={value("emailAddress")}
                errorMessage={
                  error("emailAddress") ??
                  (emailAdressNotUnique() ? t(($) => $.VALIDATION_CONFIRM_EMAIL_NOT_UNIQUE) : undefined)
                }
                onInput={(e) => {
                  updateValue("emailAddress", e.target.value);
                  setEmailAdressNotUnique(false);
                }}
                onBlur={(e) => {
                  updateValue("emailAddress", e.target.value);
                  setEmailAdressNotUnique(false);
                }}
              />
              <PasswordInput
                label={t(($) => $.SIGNUP_PAGE_FORM_PASSWORD_LABEL)}
                value={value("password")}
                errorMessage={
                  error("password") ??
                  (passwordBlacklisted() ? t(($) => $.VALIDATION_CONFIRM_PASSWORD_BLACKLISTED) : undefined)
                }
                onInput={(e) => {
                  updateValue("password", e.target.value);
                  setPasswordBlacklisted(false);
                }}
                onBlur={(e) => {
                  updateValue("password", e.target.value);
                  setPasswordBlacklisted(false);
                }}
              />
              <Checkbox
                required
                label={
                  <Translation
                    key={keyFromSelector(($) => $.SIGNUP_PAGE_FORM_USER_TERMS_CHECKBOX_LABEL)}
                    components={{
                      externalLink: (linkProps) => (
                        <TextLink isExternal href={linkProps.href} label={linkProps.label} />
                      ),
                    }}
                  />
                }
                checked={value("acceptTerms") ?? false}
                setChecked={(checked) => updateValue("acceptTerms", checked === true)}
              />
              <Show when={submitError()}>
                <InlineMessage
                  variant="warning"
                  label={t(($) => $.SIGNUP_PAGE_FORM_SUBMIT_ERROR_LABEL)}
                  action={{
                    label: t(($) => $.SIGNUP_PAGE_FORM_SUBMIT_ERROR_ACTION_LABEL),
                    onClick: directUserToSupport,
                  }}
                />
              </Show>
              <TextButton
                text={t(($) => $.SIGNUP_PAGE_FORM_CREATE_ACCOUNT)}
                type="submit"
                form={form.id}
                disabled={
                  isEmpty(value("emailAddress")) || isEmpty(value("password")) || value("acceptTerms") === false
                }
                loading={form.isSubmitting()}
              />
            </>
          )}
        </Form>
      </div>
      <Text
        class="text-center text-secondary desktop:text-left"
        text={t(($) => $.SIGNUP_PAGE_FORM_ASK_ADMIN_TO_INVITE)}
      />
    </div>
  );
};

export default SignupForm;
